import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BookingSummaryCard from './components/BookingSummaryCard';
import TrailerDetailsCard from './components/TrailerDetailsCard';
import PricingBreakdownCard from './components/PricingBreakdownCard';
import OwnerContactCard from './components/OwnerContactCard';
import NextStepsCard from './components/NextStepsCard';
import { invoiceService } from '../../services/invoiceService';

const BookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const bookingData = location?.state?.bookingData || {
    bookingId: 'BK1739234850123',
    bookingReference: 'TRL-2026-0210-4587',
    status: 'confirmed',
    createdAt: new Date()?.toISOString(),
    startDate: '2026-02-15',
    endDate: '2026-02-22',
    trailer: {
      id: 1,
      title: 'Heavy-Duty Enclosed Cargo Trailer',
      type: 'Enclosed Trailer',
      location: 'Austin, TX',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1e66491e3-1767380697290.png',
      imageAlt: 'Black enclosed cargo trailer with rear ramp door',
      specifications: {
        length: '20 ft',
        width: '8 ft',
        height: '7 ft',
        capacity: '5,000 lbs',
        features: ['Rear Ramp Door', 'Side Access Door', 'Interior Lighting', 'Tie-Down Points']
      }
    },
    pricing: {
      days: 7,
      dailyRate: 75,
      subtotal: 525,
      weeklyDiscount: 78.75,
      discountPercent: 15,
      securityDeposit: 200,
      cleaningFee: 25,
      taxRate: 8.25,
      tax: 55.22,
      total: 726.47
    },
    owner: {
      id: 1,
      name: 'Michael Rodriguez',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_182b60a79-1763295221996.png",
      avatarAlt: 'Profile photo of Michael Rodriguez',
      verified: true,
      rating: 4.9,
      reviewCount: 127,
      phone: '(512) 555-0198',
      email: 'michael.rodriguez@example.com',
      responseTime: '2 hours',
      location: 'Austin, TX'
    },
    pickup: {
      address: '1234 Industrial Blvd, Austin, TX 78701',
      instructions: 'Enter through the main gate and proceed to Bay 3. The trailer will be ready for pickup at the scheduled time.',
      accessCode: '4587',
      contactTime: '8:00 AM - 6:00 PM'
    },
    payment: {
      method: 'Credit Card',
      last4: '4242',
      transactionId: 'pi_3MtwBwLkdIwHu7ix28a3tqPa',
      paidAt: new Date()?.toISOString()
    }
  };

  const breadcrumbs = [
    { label: 'Home', path: '/home-page' },
    { label: 'Browse Trailers', path: '/search-results' },
    { label: 'Booking Confirmation', path: '/booking-confirmation' }
  ];

  const handleViewDashboard = () => {
    navigate('/renter-dashboard');
  };

  const handleContactOwner = () => {
    navigate('/messaging-center', { state: { ownerId: bookingData?.owner?.id } });
  };

  const handleStartCheckIn = () => {
    const params = new URLSearchParams({
      bookingId: bookingData?.bookingId || '',
      ownerId: bookingData?.owner?.id || '',
      trailerListingId: bookingData?.trailer?.id || ''
    });
    navigate(`/renter-check-in-process?${params?.toString()}`);
  };

  const handleDownloadReceipt = async () => {
    try {
      const bookingId = bookingData?.bookingId;
      if (!bookingId) {
        alert('Booking information not available');
        return;
      }
      await invoiceService?.generatePDF(bookingId, {
        title: bookingData?.trailer?.title,
        type: bookingData?.trailer?.type,
        location: bookingData?.trailer?.location
      });
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download receipt. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbNavigation items={breadcrumbs} />

          {/* Success Header */}
          <div className="mt-8 text-center">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle2" size={48} color="var(--color-success)" strokeWidth={2} />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              Booking Confirmed!
            </h1>
            <p className="font-body text-base md:text-lg text-muted-foreground mb-4">
              Your trailer rental has been successfully confirmed
            </p>
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-lg">
              <span className="font-caption text-sm text-muted-foreground uppercase">
                Booking Reference:
              </span>
              <span className="font-body text-base font-semibold text-foreground">
                {bookingData?.bookingReference}
              </span>
            </div>
          </div>

          {/* Check-In CTA Banner */}
          <div className="mt-8 bg-primary/5 border border-primary/20 rounded-xl p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="ClipboardCheck" size={24} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-semibold text-foreground mb-1">
                    Complete Your Pre-Rental Check-In
                  </h2>
                  <p className="font-body text-sm text-muted-foreground">
                    Document the trailer condition with guided photo capture, selfie verification, and digital signature before pickup.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['Exterior Photos', 'Interior Photos', 'Hitch & Tires', 'Selfie + ID', 'Signature']?.map((item) => (
                      <span key={item} className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 rounded-full">
                        <Icon name="Check" size={12} color="var(--color-primary)" strokeWidth={2.5} />
                        <span className="font-caption text-xs text-primary">{item}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <Button
                variant="default"
                size="lg"
                iconName="Camera"
                iconPosition="left"
                onClick={handleStartCheckIn}
                className="flex-shrink-0"
              >
                Start Check-In
              </Button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Details */}
            <div className="lg:col-span-2 space-y-6">
              <BookingSummaryCard bookingData={bookingData} />
              <TrailerDetailsCard trailer={bookingData?.trailer} />
              <PricingBreakdownCard pricing={bookingData?.pricing} payment={bookingData?.payment} onDownloadReceipt={handleDownloadReceipt} />
            </div>

            {/* Right Column - Owner & Next Steps */}
            <div className="lg:col-span-1 space-y-6">
              <OwnerContactCard owner={bookingData?.owner} pickup={bookingData?.pickup} onContact={handleContactOwner} />
              <NextStepsCard bookingData={bookingData} onStartCheckIn={handleStartCheckIn} />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="default"
              size="lg"
              iconName="ClipboardCheck"
              iconPosition="left"
              onClick={handleStartCheckIn}
            >
              Start Check-In Process
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="LayoutDashboard"
              iconPosition="left"
              onClick={handleViewDashboard}
            >
              View Dashboard
            </Button>
            <Button
              variant="ghost"
              size="lg"
              iconName="MessageCircle"
              iconPosition="left"
              onClick={handleContactOwner}
            >
              Message Owner
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;